/** New-outage browser notification + in-app toast (spec §4.4). Watches the live
 *  active-outage list; when a new event appears it (a) posts a desktop
 *  Notification when the operator has granted permission, and (b) ALWAYS shows an
 *  in-app toast fallback (which works with no permission / in an unfocused tab
 *  that blocks Notifications). Permission is requested only on an explicit
 *  user-gesture button (browsers reject programmatic requests) — that opt-in is
 *  the standalone `NotifyOptIn` control, hoisted into the dashboard header. */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { OutageListItem } from '../../types/oms.ts'
import { activeIdSet, newOutageIds } from './newOutageDiff.ts'
import { deviceLabel } from './omsFormat.tsx'

interface Toast {
  id: number
  message: string
  // setTimeout handle for this toast's 12s auto-dismiss. Owned per-toast from
  // enqueue time so it is cleared exactly once — by whoever removes the toast
  // first (auto-dismiss or manual close) — never by an effect re-run.
  timer: ReturnType<typeof setTimeout>
}

const NOTIFY_SUPPORTED = typeof window !== 'undefined' && 'Notification' in window
const TOAST_TTL_MS = 12_000

export function useNewOutageNotifier(
  items: OutageListItem[] | undefined,
  onSelect?: (id: number) => void,
) {
  const seen = useRef<Set<number> | null>(null)
  const [toasts, setToasts] = useState<Toast[]>([])

  const byId = useRef(new Map<number, OutageListItem>())
  // Live set of pending auto-dismiss timers, so unmount can clear every one
  // without reading stale toast state out of a closure.
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>())

  // Remove a toast AND clear its owned timer. Shared by the auto-dismiss
  // timeout and the manual dismiss button; clearing an already-fired timer is
  // harmless, so whichever path removes the toast first wins cleanly.
  const dismiss = useCallback((id: number) => {
    setToasts(prev => {
      const t = prev.find(x => x.id === id)
      if (t) {
        clearTimeout(t.timer)
        timers.current.delete(t.timer)
      }
      return prev.filter(x => x.id !== id)
    })
  }, [])

  useEffect(() => {
    if (!items) return
    byId.current = new Map(items.map(o => [o.id, o]))
    // First poll: seed the baseline, never toast the existing backlog.
    if (seen.current === null) {
      seen.current = activeIdSet(items)
      return
    }
    const fresh = newOutageIds(seen.current, items)
    seen.current = activeIdSet(items)
    if (fresh.length === 0) return

    for (const id of fresh) {
      const o = byId.current.get(id)
      const label = o
        ? deviceLabel(o.scope, o.device_id, o.unresolved_meter_number)
        : `outage #${id}`
      const message = `New outage #${id} — ${label}`
      // Start THIS toast's auto-dismiss timer at enqueue time and store the
      // handle on the entry. A later refetch re-runs this effect but no longer
      // owns/clears these timers, so a toast mid-window is never left sticky.
      const timer = setTimeout(() => dismiss(id), TOAST_TTL_MS)
      timers.current.add(timer)
      setToasts(prev => [...prev, { id, message, timer }])
      // Desktop notification (best-effort; ignored if not granted).
      if (NOTIFY_SUPPORTED && Notification.permission === 'granted') {
        try {
          const n = new Notification('New outage detected', { body: message, tag: `oms-${id}` })
          n.onclick = () => {
            window.focus()
            onSelect?.(id)
          }
        } catch {
          /* Notification construction can throw on some platforms — toast still shows. */
        }
      }
    }
    // No effect-cleanup here: timers are owned per-toast (dismiss / unmount),
    // NOT torn down on every `items` change.
  }, [items, onSelect, dismiss])

  // Unmount only: clear any timers still pending so none fire after teardown.
  useEffect(() => {
    const pending = timers.current
    return () => {
      pending.forEach(t => clearTimeout(t))
      pending.clear()
    }
  }, [])

  return { toasts, dismiss }
}

/** Standalone desktop-alerts opt-in (spec §4.4). Self-contained — owns only the
 *  permission grant, no outage diffing — so it can live in the dashboard header
 *  without duplicating the notifier's watch. Renders nothing once the browser
 *  has a non-default answer (granted/denied) or lacks Notification support. */
export function NotifyOptIn() {
  const [permission, setPermission] = useState<NotificationPermission>(
    NOTIFY_SUPPORTED ? Notification.permission : 'denied',
  )
  const requestPermission = useCallback(() => {
    if (!NOTIFY_SUPPORTED) return
    Notification.requestPermission().then(setPermission)
  }, [])

  if (!NOTIFY_SUPPORTED || permission !== 'default') return null
  return (
    <button
      onClick={requestPermission}
      className="text-xs px-2 py-1 rounded-md border border-blue-300 text-blue-700 hover:bg-blue-50 shrink-0"
      title="Get a desktop alert when a new outage is detected"
    >
      Enable desktop alerts
    </button>
  )
}

export function NewOutageToaster({
  items,
  onSelect,
}: {
  items: OutageListItem[] | undefined
  onSelect?: (id: number) => void
}) {
  const { toasts, dismiss } = useNewOutageNotifier(items, onSelect)

  return (
    // Toast stack (bottom-right, above the map).
    <div className="fixed bottom-4 right-4 z-[2000] flex flex-col gap-2 w-72">
      {toasts.map(t => (
        <div
          key={t.id}
          role="status"
          className="flex items-start gap-2 bg-red-600 text-white text-sm rounded-lg shadow-lg px-3 py-2"
        >
          <button
            onClick={() => onSelect?.(t.id)}
            className="flex-1 text-left hover:underline"
          >
            {t.message}
          </button>
          <button
            onClick={() => dismiss(t.id)}
            aria-label="Dismiss"
            className="text-white/80 hover:text-white leading-none"
          >
            &times;
          </button>
        </div>
      ))}
    </div>
  )
}
